/**
 * Context-aware search filter configuration
 * Maps routes to their searchable fields for client-side filtering
 */

// Route → context mapping for the global search
export const SEARCH_CONTEXTS = {
  "/jobs": {
    type: "jobs",
    placeholder: "Search jobs...",
    searchableFields: ["title", "description", "customer.name", "status"],
  },
  "/invoices": {
    type: "invoices",
    placeholder: "Search invoices...",
    searchableFields: ["title", "invoice_number", "customer.name", "status"],
  },
  "/estimates": {
    type: "estimates",
    placeholder: "Search estimates...",
    searchableFields: ["title", "estimate_number", "customer.name", "status"],
  },
  "/customers": {
    type: "customers",
    placeholder: "Search customers...",
    searchableFields: null, // Uses existing backend search
  },
};

/**
 * Get the search context for a given pathname
 * @param {string} pathname - Current route pathname
 * @returns {object|null} Search context config or null for default behavior
 */
export function getSearchContext(pathname) {
  // Check for exact matches first
  if (SEARCH_CONTEXTS[pathname]) {
    return SEARCH_CONTEXTS[pathname];
  }
  
  // Check for partial matches (e.g., /jobs/123 should still use jobs context)
  for (const route of Object.keys(SEARCH_CONTEXTS)) {
    if (pathname.startsWith(route + "/") || pathname === route) {
      return SEARCH_CONTEXTS[route];
    }
  }
  
  return null;
}

/**
 * Get nested property value from an object using dot notation
 * @param {object} obj - Object to search
 * @param {string} path - Dot-notated path (e.g., "customer.name")
 * @returns {any} Value at path or undefined
 */
function getNestedValue(obj, path) {
  return path.split(".").reduce((current, key) => {
    return current && current[key] !== undefined ? current[key] : undefined;
  }, obj);
}

/**
 * Reusable filter helper for client-side filtering
 * @param {Array} items - Array of items to filter
 * @param {string} query - Search query string
 * @param {Array<string>} searchableFields - Fields to search within each item
 * @returns {Array} Filtered items matching the query
 */
export function filterItems(items, query, searchableFields) {
  if (!query || query.trim().length < 2 || !items || !searchableFields) {
    return items;
  }

  const normalizedQuery = query.toLowerCase().trim();

  return items.filter((item) => {
    return searchableFields.some((field) => {
      const value = getNestedValue(item, field);
      if (value === undefined || value === null) return false;
      
      // Convert to string and check for match
      const stringValue = String(value).toLowerCase();
      return stringValue.includes(normalizedQuery);
    });
  });
}

/**
 * Check if current route should use context-aware local filtering
 * @param {string} pathname - Current route pathname
 * @returns {boolean} True if should use local filtering
 */
export function shouldUseLocalFilter(pathname) {
  const context = getSearchContext(pathname);
  // Use local filter for jobs, invoices, estimates (not customers)
  return context !== null && context.type !== "customers";
}
